import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { is } from 'ramda';

import styles from './Typography.module.scss';

const s = classNames.bind(styles);

/**
|--------------------------------------------------
| @function mergeClassNames
| @description function for merging className that
| will be frequently used over the different components
|--------------------------------------------------
*/
export const mergeClassNames = ({
	mainClassName,
	withEllipsis,
	className,
	textAlign,
	marginX,
	marginTop,
	marginBottom,
	paddingX,
	paddingTop,
	paddingBottom,
	withNoWordBreak,
	withWordBreak,
	withAnimation,
	withBorderBottom,
	color,
	disabled,
	fontWeight,
	fontSize,
	meta,
	error
}) =>
	s({
		[mainClassName]: mainClassName,
		[className]: className,
		[`marginX_${marginX}`]: is(String, marginX),
		[`marginTop_${marginTop}`]: is(String, marginTop),
		[`marginBottom_${marginBottom}`]: is(String, marginBottom),
		[`paddingX_${paddingX}`]: is(String, paddingX),
		[`paddingTop_${paddingTop}`]: is(String, paddingTop),
		[`paddingBottom_${paddingBottom}`]: is(String, paddingBottom),
		[`textAlign_${textAlign}`]: textAlign,
		[`fontWeight_${fontWeight}`]: fontWeight,
		[`fontSize_${fontSize}`]: fontSize,
		[`color_${color}`]: is(String, color),
		withBorderBottom,
		withEllipsis,
		withAnimation,
		withNoWordBreak,
		withWordBreak,
		disabled,
		meta,
		error
	});

/**
|--------------------------------------------------
| @component <Heading />
|--------------------------------------------------
*/
export const Heading = ({ children, type, style, onClickCallback, ...props }) => {
	const classNames = mergeClassNames({
		mainClassName: [`heading_${type}`],
		...props
	});
	switch (type) {
		case 'h1':
			return (
				<h1 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h1>
			);
		case 'h2':
			return (
				<h2 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h2>
			);
		case 'h3':
			return (
				<h3 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h3>
			);
		case 'h4':
			return (
				<h4 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h4>
			);
		case 'h5':
			return (
				<h5 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h5>
			);
		case 'h6':
			return (
				<h6 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h6>
			);
		default:
			return (
				<h1 className={classNames} style={style} onClick={onClickCallback}>
					{children}
				</h1>
			);
	}
};

Heading.defaultProps = {
	marginTop: '0',
	marginBottom: '2'
};

Heading.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
	type: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']).isRequired,
	textAlign: PropTypes.oneOf(['left', 'center', 'right']),
	fontWeight: PropTypes.oneOf([300, 400, 600, 700]),
	marginTop: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	marginBottom: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	paddingBottom: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	color: PropTypes.oneOf(['inherit', 'grey1', 'grey2', 'grey3', 'grey4', 'grey5', 'black', 'white']),
	withEllipsis: PropTypes.bool,
	withNoWordBreak: PropTypes.bool,
	withAnimation: PropTypes.bool,
	withBorderBottom: PropTypes.bool,
	meta: PropTypes.bool,
	style: PropTypes.object,
	onClickCallback: PropTypes.func,
	className: PropTypes.string
};

/**
|--------------------------------------------------
| @component <Label />
|--------------------------------------------------
*/
export const Label = ({ children, htmlFor, ...props }) => {
	const classNames = mergeClassNames({
		mainClassName: 'label',
		...props
	});
	return (
		<label className={classNames} htmlFor={htmlFor}>
			{children}
		</label>
	);
};

Label.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
	htmlFor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	textAlign: PropTypes.oneOf(['left', 'center', 'right']),
	marginTop: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	marginBottom: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	withEllipsis: PropTypes.bool,
	withNoWordBreak: PropTypes.bool,
	disabled: PropTypes.bool,
	error: PropTypes.bool,
	meta: PropTypes.bool,
	className: PropTypes.string
};

Label.defaultProps = {
	marginBottom: '1'
};

/**
|--------------------------------------------------
| @component <Paragraph />
|--------------------------------------------------
*/
export const Paragraph = ({ children, ...props }) => {
	const classNames = mergeClassNames({
		mainClassName: 'text',
		...props
	});
	return <p className={classNames}>{children}</p>;
};

Paragraph.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
	fontWeight: PropTypes.oneOf([300, 400, 600, 700]),
	textAlign: PropTypes.oneOf(['left', 'center', 'right']),
	fontSize: PropTypes.oneOf(['xs', 's', 'm', 'l', 'xl']),
	marginTop: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	marginBottom: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	color: PropTypes.oneOf(['inherit', 'grey1', 'grey2', 'grey3', 'grey4', 'grey5', 'black', 'white']),
	withEllipsis: PropTypes.bool,
	withNoWordBreak: PropTypes.bool,
	disabled: PropTypes.bool,
	meta: PropTypes.bool,
	error: PropTypes.bool,
	className: PropTypes.string
};

/**
|--------------------------------------------------
| @component <Span />
|--------------------------------------------------
*/
export const Span = ({ children, ...props }) => {
	const classNames = mergeClassNames({
		...props
	});
	return <span className={classNames}>{children}</span>;
};

Span.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
	fontWeight: PropTypes.oneOf([300, 400, 600, 700]),
	textAlign: PropTypes.oneOf(['left', 'center', 'right']),
	fontSize: PropTypes.oneOf(['xs', 's', 'm', 'l', 'xl']),
	marginTop: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	marginBottom: PropTypes.oneOf(['0', '05', '1', '15', '2', '25', '3', '4']),
	color: PropTypes.oneOf(['inherit', 'grey1', 'grey2', 'grey3', 'grey4', 'grey5', 'black', 'white']),
	withEllipsis: PropTypes.bool,
	withNoWordBreak: PropTypes.bool,
	disabled: PropTypes.bool,
	meta: PropTypes.bool,
	error: PropTypes.bool,
	className: PropTypes.string
};

export default Heading;
